import { useEffect, useRef, useState } from "react";

import { FieldHint, FilterLabel } from "../AdminPage.styles";
import { PreviewFrame, PreviewPane } from "./LongTextEditor.styles";

interface IHtmlPreviewProps {
  html: string;
  title: string;
  hint?: string;
}

/**
 * How long the editor must be idle before the frame re-renders. Each update
 * is a full document navigation, and an email template runs to ~12k
 * characters — doing that per keystroke made typing stutter.
 */
const PREVIEW_DEBOUNCE_MS = 300;

/**
 * Live render of an email template beside its editor (EN-5162), replacing the
 * preview half of Retool's `PMpCustomComponents_HtmlEditorViewer`.
 *
 * ⚠️ The markup is rendered in a **sandboxed iframe with no `allow-scripts`**
 * and via `srcdoc`, so it gets no origin, no script execution and no access to
 * the portal document. The templates are operator-authored on an admin-only
 * page, but "render this arbitrary HTML inline" is not a thing to do in the
 * portal's own document — a stray `<script>` or a `<style>` bleeding into the
 * page would both be real.
 *
 * `srcdoc` is written from an effect rather than passed as a React prop. The
 * page mounts this frame in the same render that loads the template, when the
 * HTML is still the empty string; React then patches the attribute on the
 * next render, and Chrome ignores that write while the frame's initial
 * about:blank load is still in flight — so the first template you opened
 * previewed as a blank pane until you touched the editor. An effect runs
 * after the frame is in the document, and re-runs for every later value.
 */
export function HtmlPreview({ html, title, hint }: IHtmlPreviewProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [debouncedHtml, setDebouncedHtml] = useState(html);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedHtml(html), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [html]);

  useEffect(() => {
    frameRef.current?.setAttribute("srcdoc", debouncedHtml);
  }, [debouncedHtml]);

  return (
    <PreviewPane>
      <FilterLabel as="span">Preview</FilterLabel>
      <PreviewFrame ref={frameRef} title={title} sandbox="" />
      {hint && <FieldHint>{hint}</FieldHint>}
    </PreviewPane>
  );
}
